import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  Loader2, RefreshCw, Send, Search, ShieldCheck, AlertTriangle,
  ExternalLink, Copy, FileText, Globe, History,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Admin → SEO Indexing
 *
 * Single pane of glass for the indexing layer we built:
 *   - Google Indexing API status (service-account configured?)
 *   - IndexNow status (key minted, file reachable, last submission)
 *   - Manual single-URL submit (to IndexNow + Google)
 *   - One-click "Re-ping all 6 sitemap segments" for after a bulk import
 *   - Last submission audit row
 */
export default function AdminIndexing() {
  const { token } = useAuth();
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const [google, setGoogle] = useState(null);
  const [indexnow, setIndexnow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [keyFileOk, setKeyFileOk] = useState(null);
  const [singleUrl, setSingleUrl] = useState('');
  const [submitBusy, setSubmitBusy] = useState(false);
  const [pingBusy, setPingBusy] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const [g, i] = await Promise.allSettled([
        axios.get(`${API}/admin/google-indexing/status`, auth),
        axios.get(`${API}/admin/indexnow/status`, auth),
      ]);
      setGoogle(g.status === 'fulfilled' ? g.value.data : { error: g.reason?.response?.data?.detail || 'failed' });
      setIndexnow(i.status === 'fulfilled' ? i.value.data : { error: i.reason?.response?.data?.detail || 'failed' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [token]);

  // Probe the key-file from the browser so we know it's actually reachable
  // from the public internet (not just from the backend).
  useEffect(() => {
    if (!indexnow?.key_file_url) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(indexnow.key_file_url, { cache: 'no-store' });
        if (!cancelled) setKeyFileOk(r.ok);
      } catch (_) {
        if (!cancelled) setKeyFileOk(false);
      }
    })();
    return () => { cancelled = true; };
  }, [indexnow?.key_file_url]);

  const copy = (text, label = 'Copied') => {
    try { navigator.clipboard.writeText(text); toast.success(label); }
    catch (_) { toast.error('Copy failed'); }
  };

  const submitSingle = async () => {
    if (!singleUrl.startsWith('http')) {
      toast.error('Enter a full https:// URL');
      return;
    }
    setSubmitBusy(true);
    setLastResult(null);
    try {
      const res = await axios.post(`${API}/admin/indexnow/submit`,
        { url: singleUrl }, auth);
      setLastResult(res.data);
      const status = res.data?.results?.[0]?.status;
      if (res.data?.ok || [200, 202].includes(status)) {
        toast.success(`Submitted to IndexNow (${status})`);
      } else {
        toast.error(`IndexNow returned ${status || 'error'} — see details below`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Submit failed');
    } finally { setSubmitBusy(false); }
  };

  const pingAllSitemaps = async () => {
    setPingBusy(true);
    setLastResult(null);
    try {
      const res = await axios.post(`${API}/admin/indexnow/ping-sitemaps`, {}, auth);
      setLastResult(res.data);
      if (res.data?.ok) {
        toast.success(`Submitted ${res.data.submitted} sitemap URLs to Bing/Yandex/Naver`);
      } else {
        toast.error('Submission rejected — see details below');
      }
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Ping failed');
    } finally { setPingBusy(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pt-20 pb-12" data-testid="admin-indexing-page">
      <div className="max-w-5xl mx-auto px-4 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
              <Globe className="h-7 w-7 text-indigo-400" />
              Search Engine Indexing
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Ping Google, Bing, Yandex, Naver, Yep, and Seznam the moment your content publishes — instead of waiting 24-72h for natural re-crawl.
            </p>
          </div>
          <Button variant="outline" onClick={refresh} data-testid="indexing-refresh-btn">
            <RefreshCw className="h-4 w-4 mr-1.5" /> Refresh
          </Button>
        </div>

        {/* Status cards */}
        <div className="grid md:grid-cols-2 gap-4">
          {/* Google Indexing API status */}
          <Card className="border-border" data-testid="google-indexing-status-card">
            <CardContent className="p-5 space-y-3">
              <div className="flex items-center justify-between">
                <h2 className="font-bold flex items-center gap-2">
                  <Search className="h-5 w-5 text-blue-400" /> Google Indexing API
                </h2>
                <StatusBadge ok={google?.enabled} okLabel="Live" badLabel="Not configured" />
              </div>
              {google?.enabled ? (
                <>
                  <Row label="Service account" value={google.service_account_email} mono />
                  <Row label="Public URL" value={google.public_base_url} />
                  <p className="text-xs text-muted-foreground">
                    Fires automatically on every event publish + update.
                  </p>
                </>
              ) : (
                <div className="text-xs text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded p-3 space-y-1.5">
                  <div className="flex items-center gap-1.5 font-semibold">
                    <AlertTriangle className="h-3.5 w-3.5" /> Setup required
                  </div>
                  <p>{google?.setup_notes || 'GOOGLE_INDEXING_SERVICE_ACCOUNT_JSON not set.'}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* IndexNow status */}
          <Card className="border-border" data-testid="indexnow-status-card">
            <CardContent className="p-5 space-y-3">
              <div className="flex items-center justify-between">
                <h2 className="font-bold flex items-center gap-2">
                  <ShieldCheck className="h-5 w-5 text-emerald-400" /> IndexNow
                </h2>
                <StatusBadge ok={indexnow?.enabled} okLabel="Live" badLabel="Disabled" />
              </div>
              <Row label="Engines" value={(indexnow?.engines || []).join(', ')} />
              <Row label="Key" value={indexnow?.key_preview} mono />
              <div className="text-xs space-y-1">
                <p className="text-muted-foreground">Key file:</p>
                <div className="flex items-center gap-1.5 flex-wrap">
                  <code className="bg-muted/40 rounded px-1.5 py-0.5 text-[11px] break-all" data-testid="indexnow-key-url">
                    {indexnow?.key_file_url}
                  </code>
                  {indexnow?.key_file_url && (
                    <>
                      <button onClick={() => copy(indexnow.key_file_url, 'Key URL copied')} className="text-indigo-400 hover:text-indigo-300">
                        <Copy className="h-3 w-3" />
                      </button>
                      <a href={indexnow.key_file_url} target="_blank" rel="noopener noreferrer" className="text-indigo-400 hover:text-indigo-300">
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    </>
                  )}
                  {keyFileOk === true && <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/30 text-[10px]">Reachable ✓</Badge>}
                  {keyFileOk === false && <Badge className="bg-rose-500/15 text-rose-300 border-rose-500/30 text-[10px]">Not reachable</Badge>}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Actions */}
        <Card className="border-border" data-testid="indexing-actions-card">
          <CardContent className="p-5 space-y-4">
            <h2 className="font-bold flex items-center gap-2">
              <Send className="h-5 w-5 text-fuchsia-400" /> Manual submission
            </h2>

            {/* Single URL */}
            <div>
              <label className="text-[11px] uppercase font-bold text-muted-foreground">Submit one URL</label>
              <div className="flex gap-2 mt-1">
                <Input
                  value={singleUrl}
                  onChange={(e) => setSingleUrl(e.target.value)}
                  placeholder="https://eventism.com/events/denver/your-event-slug"
                  className="flex-1 font-mono text-xs"
                  data-testid="indexing-single-url-input"
                />
                <Button
                  onClick={submitSingle}
                  disabled={submitBusy || !singleUrl}
                  className="bg-fuchsia-600 hover:bg-fuchsia-500 text-white"
                  data-testid="indexing-submit-single-btn"
                >
                  {submitBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4 mr-1.5" /> Submit</>}
                </Button>
              </div>
              <p className="text-[10px] text-muted-foreground mt-1">
                Sends to all IndexNow engines (Bing/Yandex/Naver/Yep/Seznam). Google is pinged automatically when an event is published.
              </p>
            </div>

            <div className="border-t border-border pt-4">
              <label className="text-[11px] uppercase font-bold text-muted-foreground">Re-crawl every sitemap segment</label>
              <p className="text-xs text-muted-foreground mt-1 mb-3">
                Use after a bulk publish, mass cancel, or content migration. Submits all 6 sitemap URLs (index + 5 segments) so engines re-crawl the whole site.
              </p>
              <Button
                onClick={pingAllSitemaps}
                disabled={pingBusy}
                className="bg-gradient-to-r from-indigo-600 to-fuchsia-600 hover:from-indigo-500 hover:to-fuchsia-500 text-white"
                data-testid="indexing-ping-sitemaps-btn"
              >
                {pingBusy ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Pinging…</> : <><Globe className="h-4 w-4 mr-1.5" /> Re-ping all 6 sitemaps</>}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Result of last manual action */}
        {lastResult && (
          <Card className="border-border" data-testid="indexing-last-result">
            <CardContent className="p-5 space-y-2">
              <h2 className="font-bold flex items-center gap-2">
                <FileText className="h-5 w-5 text-amber-400" /> Last submission
              </h2>
              <div className="flex flex-wrap gap-3 text-xs">
                <Stat label="Submitted" value={lastResult.submitted} />
                <Stat label="Overall OK" value={lastResult.ok ? 'yes' : 'no'} accent={lastResult.ok ? 'emerald' : 'rose'} />
              </div>
              <div className="space-y-1.5 mt-2">
                {(lastResult.results || []).map((r, i) => (
                  <div key={i} className="bg-muted/30 rounded p-2 text-xs">
                    <div className="flex items-center justify-between">
                      <code className="font-mono text-[11px]">{r.host}</code>
                      <Badge className={r.ok ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" : "bg-rose-500/15 text-rose-300 border-rose-500/30"}>
                        {r.status || 'n/a'} {r.count} URL{r.count === 1 ? '' : 's'}
                      </Badge>
                    </div>
                    {r.body && <pre className="text-[10px] text-rose-200 mt-1 whitespace-pre-wrap break-all">{r.body}</pre>}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Last historical submission from the audit log */}
        {indexnow?.last_submission && (
          <Card className="border-border opacity-90" data-testid="indexing-history-card">
            <CardContent className="p-5 space-y-2">
              <h2 className="font-bold flex items-center gap-2">
                <History className="h-5 w-5 text-slate-400" /> Last automatic submission
              </h2>
              <div className="text-xs text-muted-foreground">
                {new Date(indexnow.last_submission.submitted_at).toLocaleString()} · {indexnow.last_submission.url_count} URL{indexnow.last_submission.url_count === 1 ? '' : 's'}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

// ---------- helpers ----------

function StatusBadge({ ok, okLabel = 'OK', badLabel = 'Off' }) {
  if (ok) {
    return <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/30">{okLabel}</Badge>;
  }
  return <Badge className="bg-amber-500/15 text-amber-300 border-amber-500/30">{badLabel}</Badge>;
}

function Row({ label, value, mono = false }) {
  if (!value) return null;
  return (
    <div className="flex items-baseline gap-2 text-xs">
      <span className="text-muted-foreground w-28 shrink-0">{label}</span>
      <span className={mono ? "font-mono break-all" : "break-words"}>{value}</span>
    </div>
  );
}

function Stat({ label, value, accent = 'indigo' }) {
  const palette = {
    indigo: 'text-indigo-300',
    emerald: 'text-emerald-300',
    rose: 'text-rose-300',
  }[accent] || 'text-indigo-300';
  return (
    <div>
      <div className="text-[10px] uppercase text-muted-foreground">{label}</div>
      <div className={`text-sm font-bold ${palette}`}>{value}</div>
    </div>
  );
}
